import type { Translate } from '../i18n';

export interface ExampleEntry {
  id: string;
  file: string;
  title: string;
  lang: 'fr' | 'en';
  sizes: string[];
  description_fr: string;
  description_en: string;
  /** What the scenario is meant to show (corpus/examples/index.json). */
  expected?: { errors?: number; warnings?: number };
}

const base = import.meta.env.BASE_URL;

function isEntry(v: unknown): v is ExampleEntry {
  if (!v || typeof v !== 'object') return false;
  const e = v as Record<string, unknown>;
  return typeof e.id === 'string' && typeof e.file === 'string' && typeof e.title === 'string';
}

/** Examples are static files next to the app (public/examples, copied by scripts/copy-engine.mjs). */
export async function loadExampleIndex(): Promise<ExampleEntry[]> {
  const res = await fetch(`${base}examples/index.json`);
  if (!res.ok) throw new Error(`examples/index.json: HTTP ${res.status}`);
  const data: unknown = await res.json();
  if (!Array.isArray(data)) throw new Error('examples/index.json: not an array');
  return data.filter(isEntry).map((e) => ({
    ...e,
    lang: e.lang === 'en' ? 'en' : 'fr',
    sizes: Array.isArray(e.sizes) ? e.sizes : [],
    description_fr: e.description_fr ?? '',
    description_en: e.description_en ?? '',
    expected: e.expected && typeof e.expected === 'object' ? e.expected : undefined,
  }));
}

/** "1 erreur attendue", "patron propre", … or null when the index says nothing. */
export function expectedHint(entry: ExampleEntry, t: Translate): string | null {
  const e = entry.expected;
  if (!e) return null;
  const errors = Number(e.errors ?? 0);
  const warnings = Number(e.warnings ?? 0);
  if (errors === 0 && warnings === 0) return t('examples.expectClean');
  const parts: string[] = [];
  if (errors > 0) parts.push(errors === 1 ? t('examples.expectError1') : t('examples.expectErrorsN', { n: errors }));
  if (warnings > 0) parts.push(warnings === 1 ? t('examples.expectWarning1') : t('examples.expectWarningsN', { n: warnings }));
  return parts.join(', ');
}

/** Same text modulo line endings and trailing whitespace. */
export function sameText(a: string, b: string): boolean {
  const norm = (s: string) =>
    s
      .replace(/\r\n?/g, '\n')
      .split('\n')
      .map((l) => l.trimEnd())
      .join('\n')
      .trimEnd();
  return norm(a) === norm(b);
}

export async function loadExampleText(entry: ExampleEntry): Promise<string> {
  const res = await fetch(`${base}examples/${encodeURIComponent(entry.file)}`);
  if (!res.ok) throw new Error(`${entry.file}: HTTP ${res.status}`);
  return res.text();
}

/**
 * Whether `text` still derives from `example`: at least one substantial line in common.
 * Used to drop the example association once the user replaced the text with their own.
 */
export function derivesFrom(text: string, example: string): boolean {
  const lines = (s: string) =>
    s
      .split('\n')
      .map((l) => l.trim())
      .filter((l) => l.length >= 6);
  const source = new Set(lines(example));
  return lines(text).some((l) => source.has(l));
}
